'use client';

import { useRef, useState } from 'react';

import { SearchApiRatingCommentsOrderBy } from '@/app/(main)/(pages)/_types/search-api-response.type';
import { getProfessionalProfileComments } from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/_services/get-professional-profile-from-search-api.service';
import { useToast } from '@/components/ui/use-toast';
import { getLogger } from '@/logger/logger';
import { RatingComment } from '@/types';

export const COMMENTS_SORT_ERROR_MESSAGE = 'Erro ao ordenar os comentários.';

interface SortedCommentsPage {
  orderBy: SearchApiRatingCommentsOrderBy;
  comments: RatingComment[];
  hasMore: boolean;
}

export interface UseProfessionalCommentsReturn {
  orderBy: SearchApiRatingCommentsOrderBy;
  comments: RatingComment[];
  hasMore: boolean;
  commentsKey: string;
  isSorting: boolean;
  changeOrderBy: (next: SearchApiRatingCommentsOrderBy) => void;
}

export function useProfessionalComments(
  professionalId: string,
  initialComments: RatingComment[],
  initialHasMore: boolean,
): UseProfessionalCommentsReturn {
  const { toast } = useToast();
  const [orderBy, setOrderBy] = useState(SearchApiRatingCommentsOrderBy.NEWEST);
  const [sortedPage, setSortedPage] = useState<SortedCommentsPage | null>(null);
  const [isSorting, setIsSorting] = useState(false);
  const requestRef = useRef(0);
  const orderByRef = useRef(orderBy);
  const appliedOrderByRef = useRef(orderBy);

  const changeOrderBy = (next: SearchApiRatingCommentsOrderBy) => {
    if (next === orderByRef.current) {
      return;
    }

    orderByRef.current = next;
    setOrderBy(next);
    setIsSorting(true);

    requestRef.current += 1;
    const request = requestRef.current;

    getProfessionalProfileComments(professionalId, 1, next)
      .then(({ comments, hasMore }) => {
        if (request !== requestRef.current) {
          return;
        }

        appliedOrderByRef.current = next;
        setSortedPage({ orderBy: next, comments, hasMore });
        setIsSorting(false);
      })
      .catch((error) => {
        if (request !== requestRef.current) {
          return;
        }

        const logger = getLogger('useProfessionalComments');
        logger.error('Error sorting the professional comments', {
          professionalId,
          orderBy: next,
          error,
        });

        orderByRef.current = appliedOrderByRef.current;
        setOrderBy(appliedOrderByRef.current);
        setIsSorting(false);
        toast.error(COMMENTS_SORT_ERROR_MESSAGE);
      });
  };

  return {
    orderBy,
    comments: sortedPage?.comments ?? initialComments,
    hasMore: sortedPage?.hasMore ?? initialHasMore,
    commentsKey: sortedPage?.orderBy ?? SearchApiRatingCommentsOrderBy.NEWEST,
    isSorting,
    changeOrderBy,
  };
}
